import { Grid } from "@mui/material";
import theme from "Theme/AppTheme";
import type {
    Module,
    Question,
} from "components/Admin/Organizations/types/Module.type";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import AcxVirtualizedSelectMulti from "components/UI/Select/AcxVirtualizedSelectMulti";
import { observer } from "mobx-react";
import React from "react";
import type ChartCardStore from "../ChartCardStore";

type QuestionControlProps = {
    store: ChartCardStore;
    modules: Module[];
    showComparePrevCheckbox: boolean;
};

const normalizeIds = (value?: string | string[]): string[] =>
    Array.isArray(value)
        ? value.map((id) => `${id}`)
        : value
          ? [`${value}`]
          : [];

const haveSameSelections = (left: string[], right: string[]): boolean => {
    if (left.length !== right.length) {
        return false;
    }

    const leftSet = new Set(left);
    return right.every((id) => leftSet.has(id));
};

const QuestionControl = observer(
    ({ store, modules, showComparePrevCheckbox }: QuestionControlProps) => {
        const isSingleQuestionSelect =
            store.selectionType === "Question" ||
            store.selectionType === "Tags";

        const selectedModule = modules.find(
            (module) => module.id === store.selectedIds["module"],
        );
        const selectedQuestionId = store.selectedIds["question"] as
            | string
            | undefined;
        const selectedQuestion: Question | undefined =
            selectedModule?.questions.find((q) => q.id === selectedQuestionId);
        const selectedModuleQuestions = selectedModule?.questions ?? [];
        const selectedQuestionTags = selectedQuestion?.tags ?? [];

        const optionsLoading = store.optionsStore.orgModulesLoading;

        return (
            <Grid
                item
                container
                xs={showComparePrevCheckbox ? 6 : 8}
                spacing={2}
                alignItems={"flex-end"}
            >
                <Grid item xs={4}>
                    <AcxSelect
                        fullWidth
                        defaultValue={
                            store.selectedIds["module"]
                                ? modules?.find(
                                      (item) =>
                                          item.id ===
                                          store.selectedIds["module"],
                                  )
                                : undefined
                        }
                        options={modules}
                        onChange={(item) => {
                            store.setSelectedId("module", item.id);

                            if (store.selectionType === "Tags") {
                                store.setSelectedId("question", undefined);
                                store.setSelectedId("tags", undefined);
                                store.handleSelectionChange({
                                    forceRequest: true,
                                });
                                return;
                            }

                            if (store.selectionType === "Question") {
                                store.setSelectedId("question", undefined);
                                store.handleSelectionChange({
                                    forceRequest: true,
                                });
                                return;
                            }

                            if (store.selectionType === "Questions") {
                                store.setSelectedId("questions", undefined);
                                store.handleSelectionChange({
                                    forceRequest: true,
                                });
                                return;
                            }

                            store.handleSelectionChange();
                        }}
                        placeholder="Select Module"
                        id={"signals-chart-select-module"}
                        labelField="displayName"
                        valueField="id"
                        alternateItemColor={theme.palette.gray.main}
                        isLoading={optionsLoading}
                        isDisabled={store.isReadOnly}
                        disabledAppearance="readOnly"
                    />
                </Grid>
                {isSingleQuestionSelect ? (
                    <>
                        <Grid item xs={4}>
                            <AcxSelect
                                key={`question-select-${store.selectedIds["module"] ?? "none"}`}
                                fullWidth
                                defaultValue={selectedQuestion}
                                options={selectedModuleQuestions}
                                onChange={(item) => {
                                    if (item) {
                                        store.setSelectedId(
                                            "question",
                                            item.id,
                                        );
                                        if (store.selectionType === "Tags") {
                                            store.setSelectedId(
                                                "tags",
                                                undefined,
                                            );
                                        }

                                        store.handleSelectionChange({
                                            forceRequest:
                                                store.selectionType ===
                                                    "Tags" ||
                                                store.selectionType ===
                                                    "Question",
                                        });
                                    }
                                }}
                                placeholder="Select Question"
                                id={
                                    "signals-chart-filter-question-single-select"
                                }
                                labelField="displayName"
                                valueField="id"
                                alternateItemColor={theme.palette.gray.main}
                                isLoading={optionsLoading}
                                isDisabled={store.isReadOnly}
                                disabledAppearance="readOnly"
                            />
                        </Grid>
                        {store.selectionType === "Tags" &&
                            !store.config.dataGrid && (
                                <Grid item xs={4}>
                                    <AcxVirtualizedSelectMulti
                                        options={selectedQuestionTags}
                                        labelField="value"
                                        idField="id"
                                        id={"signals-tags-multi-select-"}
                                        sortSelectedToTop={false}
                                        maxSelectionCount={store.getMaxSelection()}
                                        onApply={(ids) => {
                                            if (
                                                selectedQuestionTags.length === 0
                                            ) {
                                                return;
                                            }

                                            const nextIds = ids.map(
                                                (id) => `${id}`,
                                            );
                                            const currentIds = normalizeIds(
                                                store.selectedIds["tags"] as
                                                    | string[]
                                                    | undefined,
                                            );

                                            if (
                                                haveSameSelections(
                                                    nextIds,
                                                    currentIds,
                                                )
                                            ) {
                                                return;
                                            }

                                            store.setSelectedId("tags", nextIds);
                                            store.handleSelectionChange();
                                        }}
                                        defaultSelectedIds={selectedQuestionTags
                                            .filter((tag) =>
                                                normalizeIds(
                                                    store.selectedIds[
                                                        "tags"
                                                    ] as string[] | undefined,
                                                ).includes(tag.id ?? ""),
                                            )
                                            .map((tag) => `${tag.id ?? ""}`)
                                            .filter((id) => id.length > 0)}
                                        itemName="Response"
                                        isLoading={optionsLoading}
                                        enableCompactLayout
                                        disabled={store.isReadOnly}
                                        disabledAppearance="readOnly"
                                    />
                                </Grid>
                            )}
                    </>
                ) : (
                    !store.config.dataGrid && (
                        <Grid item xs={4}>
                            <AcxVirtualizedSelectMulti
                                options={selectedModuleQuestions}
                                id={"question-select-"}
                                onApply={(ids) => {
                                    if (
                                        selectedModuleQuestions.length === 0
                                    ) {
                                        return;
                                    }

                                    const nextIds = ids.map((id) => `${id}`);
                                    const currentIds = normalizeIds(
                                        store.selectedIds["questions"] as
                                            | string[]
                                            | undefined,
                                    );

                                    if (
                                        haveSameSelections(
                                            nextIds,
                                            currentIds,
                                        )
                                    ) {
                                        return;
                                    }

                                    store.setSelectedId("questions", nextIds);
                                    store.handleSelectionChange();
                                }}
                                itemName="Question"
                                disabled={store.isReadOnly}
                                disabledAppearance="readOnly"
                                defaultSelectedIds={modules
                                    .find((m) => m.id === store.selectedIds["module"])
                                    ?.questions.filter((q) =>
                                        (
                                            store.selectedIds["questions"] as
                                                | string[]
                                                | undefined
                                        )?.includes(q.id ?? ""),
                                    )
                                    .map((q) => q.id ?? "")}
                                labelField="displayName"
                                idField="id"
                                maxSelectionCount={store.getMaxSelection()}
                                isLoading={optionsLoading}
                                enableCompactLayout
                            />
                        </Grid>
                    )
                )}
            </Grid>
        );
    },
);

export default QuestionControl;
